/**
 * ProgressIndicator - LED点灯による進捗表示コンポーネント
 */

import { useMemo } from 'react'

interface ProgressIndicatorProps {
  current: number
  total: number
  isCircular?: boolean
}

export function ProgressIndicator({ current, total, isCircular }: ProgressIndicatorProps) {
  // 1-indexed display
  const displayCurrent = current + 1
  
  // LEDの最大表示数（画面サイズに応じて調整）
  const MAX_LEDS = 25
  
  // 表示するLEDのインデックスを計算
  const displayIndices = useMemo(() => {
    if (total <= 0) return []
    
    if (total <= MAX_LEDS) {
      // 全て表示
      return Array.from({ length: total }, (_, i) => i)
    } else {
      // 間引いて表示（現在位置周辺を重点的に）
      const indices: number[] = []
      const currentRatio = current / (total - 1)
      const focusRange = 0.3 // 現在位置の前後30%を重点表示
      
      for (let i = 0; i < MAX_LEDS; i++) {
        const ratio = i / (MAX_LEDS - 1)
        let actualIndex: number
        
        if (Math.abs(ratio - currentRatio) < focusRange) {
          // 現在位置付近は密に表示
          actualIndex = Math.round(current + (ratio - currentRatio) * total * 0.5)
        } else {
          // 離れた位置は疎に表示
          actualIndex = Math.round(ratio * (total - 1))
        }
        
        // 範囲内に収める
        actualIndex = Math.max(0, Math.min(total - 1, actualIndex))
        
        if (!indices.includes(actualIndex)) {
          indices.push(actualIndex)
        }
      }
      
      return indices.sort((a, b) => a - b)
    }
  }, [current, total])
  
  return (
    <div className="w-full max-w-md mx-auto px-4">
      {/* 数値表示 */}
      <div className="text-center mb-3">
        <span className="text-lg font-medium text-white">
          {displayCurrent} / {total}
        </span>
        {isCircular && (
          <span className="ml-2 text-sm text-gray-400">
            (ループ)
          </span>
        )}
      </div>
      
      {/* LED表示 */}
      <div className="relative">
        {/* LEDコンテナ */}
        <div className="flex items-center justify-center gap-1">
          {displayIndices.map((index) => {
            const isActive = index === current
            const distance = Math.min(
              Math.abs(index - current),
              isCircular ? Math.abs(index - current + total) : Infinity,
              isCircular ? Math.abs(index - current - total) : Infinity
            )
            const isNearby = distance <= 2
            
            return (
              <div
                key={index}
                className="relative"
                title={`${index + 1} / ${total}`}
              >
                {/* LED本体 */}
                <div
                  className={`
                    rounded-full transition-all duration-500 ease-out
                    ${isActive 
                      ? 'w-3 h-3 bg-white shadow-lg shadow-white/50' 
                      : isNearby
                      ? 'w-2 h-2 bg-gray-400'
                      : 'w-1.5 h-1.5 bg-gray-600'
                    }
                  `}
                  style={{
                    opacity: isActive ? 1 : isNearby ? 0.7 : 0.3,
                    transform: isActive ? 'scale(1.3)' : 'scale(1)',
                    animation: isActive ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
                  }}
                >
                  {/* アクティブLEDの発光エフェクト */}
                  {isActive && (
                    <>
                      <div className="absolute inset-0 rounded-full bg-white animate-ping" />
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 to-white opacity-50 blur-sm" />
                    </>
                  )}
                </div>
                
                {/* サンプル位置のマーカー（総数が少ない場合） */}
                {total > MAX_LEDS && total <= 50 && (
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-xs text-gray-600">
                    {index % 5 === 0 && (index + 1)}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        
        {/* エッジインジケーター（多すぎる場合） */}
        {total > MAX_LEDS && (
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>1</span>
            <span>{total}</span>
          </div>
        )}
      </div>
      
      {/* アニメーションスタイル */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}} />
    </div>
  )
}